import React, { useEffect, useRef, useState } from "react";
import { Send, Download, Layers, Save, X } from "lucide-react";
import Card from "../common/Card";
import Button from "../common/Button";
import Badge from "../common/Badge";
import { parseMoney, formatMoneyDisplay, formatMoneyInput } from "../../utils/money";
import { getDefaultLineItems } from "../../utils/scoring";
import { useToast } from "../../hooks/useToast";
import { generateQuotePDF, downloadQuotePDF } from "../../utils/generateQuotePDF";

import { supabase } from "../../supabaseClient";

// ── Read-receipt badge ─────────────────────────────────────────────────────
// Shown next to the customer status badge whenever view_count > 0. Pure
// presentation; the increment happens server-side via rqp_register_quote_view.
function ReadReceiptBadge({ viewCount, lastViewedAt }) {
  const ago = (() => {
    if (!lastViewedAt) return null;
    const ms = Date.now() - new Date(lastViewedAt).getTime();
    if (ms < 60_000) return "just now";
    const m = Math.floor(ms / 60_000);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  })();

  const label = viewCount === 1
    ? `Viewed once${ago ? ` · ${ago}` : ""}`
    : `Viewed ${viewCount} times${ago ? ` · last ${ago}` : ""}`;

  return (
    <span
      title={
        lastViewedAt
          ? `Last viewed ${new Date(lastViewedAt).toLocaleString()}`
          : undefined
      }
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        background: "rgba(34, 197, 94, 0.12)",
        color: "#15803d",
        border: "1px solid rgba(34, 197, 94, 0.28)",
        borderRadius: "999px",
        padding: "3px 10px",
        fontSize: "0.75rem",
        fontWeight: 600,
        letterSpacing: "0.01em",
        whiteSpace: "nowrap",
      }}
    >
      <span aria-hidden="true">👁</span>
      {label}
    </span>
  );
}

const GST_RATE = 0.1;

function CustomerStatusBadge({ status }) {
  if (status === "draft") {
    return <Badge type="info">Draft, not sent</Badge>;
  }
  if (!status || status === "pending") {
    return <Badge type="info">Awaiting customer response</Badge>;
  }
  if (status === "accepted") {
    return <Badge type="success">Accepted by customer</Badge>;
  }
  if (status === "declined") {
    return <Badge type="error">Declined by customer</Badge>;
  }
  return <Badge>{status}</Badge>;
}

function hasCashPaymentMarker(text = "") {
  return /cash payment option selected/i.test(String(text || ""));
}

/**
 * @param {Object} props
 * @param {import("../../types").SelectedLead} props.selected
 * @param {(id:string, quote:Object) => Promise<import("../../types").Quote|undefined>} props.saveQuote
 * @param {(token:string) => string} [props.getCustomerQuoteLink]
 * @param {import("../../types").Profile|null} [props.profile]
 */
export default function QuoteBuilder({ selected, saveQuote, getCustomerQuoteLink, profile }) {
  const { showToast } = useToast();
  const idCounter = useRef(0);

  function nextId() {
    idCounter.current += 1;
    return idCounter.current;
  }

  const [lineItems, setLineItems] = useState([{ _id: 1, description: "Paint and labour", amount: 0 }]);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [pickerTemplates, setPickerTemplates] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [quoteNotes, setQuoteNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const selectedId = selected?.id;
  const selectedDetailsLoaded = selected?.detailsLoaded;
  const selectedQuoteId = selected?.quote?.id;
  const selectedQuoteNotes = selected?.quote?.notes;
  const selectedQuoteLineItems = selected?.quote?.line_items;
  const selectedQuoteStatus = String(selected?.quote?.status || "").toLowerCase();
  const quoteIsDraft = selectedQuoteStatus === "draft";
  const canSaveDraft = !selected?.quote || quoteIsDraft;
  const selectedTrade = selected?.trade;
  const selectedFormNotes = selected?.form?.notes || "";
  const quoteScope = Array.isArray(selected?.form?.quoteFor)
    ? selected.form.quoteFor.filter(Boolean)
    : [];
  const progressPaymentsRequested = selected?.form?.progressPayments === true;
  const cashPaymentQuote =
    selected?.form?.cashPayment === true ||
    hasCashPaymentMarker(selectedFormNotes) ||
    hasCashPaymentMarker(selectedQuoteNotes);
  // Snapshot GST into the quote. Cash quotes deliberately skip GST.
  const gstRegistered = cashPaymentQuote ? false : profile?.gst_registered !== false;
  const profileDefaultItems = profile?.default_quote_items;
  const profileDefaultNotes = profile?.default_quote_notes;

  useEffect(() => {
    if (!selectedId) return;

    const isEditingExistingQuote =
      selectedQuoteLineItems && selectedQuoteLineItems.length > 0;
    setQuoteNotes(
      isEditingExistingQuote
        ? (selectedQuoteNotes || "")
        : (profileDefaultNotes || selectedFormNotes || "")
    );

    if (selectedQuoteLineItems && selectedQuoteLineItems.length > 0) {
      const base = Date.now();
      setLineItems(
        selectedQuoteLineItems.map((item, i) => ({
          _id: base + i,
          description: item.description || "",
          amount: parseMoney(item.amount),
        }))
      );
    } else if (Array.isArray(profileDefaultItems) && profileDefaultItems.length > 0) {
      // Pre-populate from the tradie's saved defaults when starting a brand-new
      // quote — falls through to scoring presets only when no defaults are set.
      const base = Date.now();
      setLineItems(
        profileDefaultItems.map((item, i) => ({
          _id: base + i,
          description: item.description || item.name || "",
          amount: parseMoney(item.amount),
        }))
      );
    } else {
      const base = Date.now();
      setLineItems(
        getDefaultLineItems(selectedTrade).map((item, i) => ({
          _id: base + i,
          ...item,
        }))
      );
    }
  }, [selectedId, selectedDetailsLoaded, selectedQuoteId, selectedQuoteNotes, selectedQuoteLineItems, selectedTrade, selectedFormNotes, profileDefaultItems, profileDefaultNotes]);

  function addLineItem() {
    setLineItems((prev) => [...prev, { _id: nextId(), description: "", amount: 0 }]);
  }

  function addDiscountItem() {
    // Tag the row as a discount so updateLineItem can force-negative its
    // amount no matter what the tradie types into the input. The label is
    // pre-filled to "Discount" but stays editable.
    setLineItems((prev) => [...prev, { _id: nextId(), description: "Discount", amount: 0, isDiscount: true }]);
  }

  function addVariationItem() {
    setLineItems((prev) => [
      ...prev,
      {
        _id: nextId(),
        description: "Variation - extra work requested",
        amount: 0,
      },
    ]);
    setQuoteNotes((prev) => {
      if (/amended quote/i.test(prev || "")) return prev;
      return [
        prev,
        "Amended quote: updated for extra work requested by the customer.",
      ].filter(Boolean).join("\n\n");
    });
  }

  function getOutgoingQuoteNotes() {
    if (!cashPaymentQuote || hasCashPaymentMarker(quoteNotes)) {
      return quoteNotes;
    }
    return [
      quoteNotes,
      "Cash payment option selected. No GST invoice required.",
    ].filter(Boolean).join("\n\n");
  }

  // Open the template picker and lazy-fetch the tradie's saved templates.
  // Refetched every time the picker opens so newly-created templates show
  // up without a page reload.
  async function openTemplatePicker() {
    setShowTemplatePicker(true);
    if (!profile?.business_id) {
      showToast("Business profile still loading.", "error");
      return;
    }
    setPickerLoading(true);
    const { data, error } = await supabase
      .from("quote_templates")
      .select("id, name, description, amount, line_items, sort_order")
      .eq("business_id", profile.business_id)
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: true });
    setPickerLoading(false);
    if (error) {
      showToast(`Couldn't load templates: ${error.message}`, "error");
      setPickerTemplates([]);
      return;
    }
    setPickerTemplates(data || []);
  }

  function addLineItemFromTemplate(t) {
    // Multi-line templates (post 2026-04-30 merge) — append every item.
    // Falls back to the legacy single-row shape for older rows.
    const items = Array.isArray(t.line_items) && t.line_items.length > 0
      ? t.line_items
      : [{ description: t.description || t.name, amount: Number(t.amount) || 0 }];

    setLineItems((prev) => [
      ...prev,
      ...items.map((it) => ({
        _id: nextId(),
        description: it.description || "",
        amount: Number(it.amount) || 0,
      })),
    ]);
    setShowTemplatePicker(false);
  }

  function updateLineItem(index, field, value) {
    setLineItems((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;
        if (field !== "amount") return { ...item, [field]: value };
        // Discount rows: ignore any sign the tradie types and force the
        // amount negative. Treats "-50" / "50" / "$50" all the same so the
        // discount always reduces the subtotal pre-GST.
        const parsed = parseMoney(value);
        const next = item.isDiscount
          ? -Math.abs(parsed)
          : parsed;
        return { ...item, amount: next };
      })
    );
  }

  function removeLineItem(index) {
    setLineItems((prev) => prev.filter((_, i) => i !== index));
  }

  const subtotal = lineItems.reduce(
    (sum, item) => sum + (parseMoney(item.amount) || 0),
    0
  );
  const gst = gstRegistered ? subtotal * GST_RATE : 0;
  const total = subtotal + gst;

  async function handleSaveDraft() {
    if (!selected || !canSaveDraft) return;
    if (subtotal < 0) {
      showToast("Discount cannot exceed the subtotal. Total cannot go below $0.", "error");
      return;
    }

    const outgoingQuoteNotes = getOutgoingQuoteNotes();

    setSaving(true);
    try {
      await saveQuote(selected.id, {
        subtotal,
        gst,
        total,
        lineItems,
        notes: outgoingQuoteNotes,
        gstRegistered,
        status: "draft",
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveAndSend() {
    if (!selected) return;

    // ATO compliance: a discount can never push the subtotal below $0 — GST
    // would then be a negative number, which is not a valid invoice/quote.
    if (subtotal < 0) {
      showToast(
        "Discount cannot exceed the subtotal — total cannot go below $0.",
        "error"
      );
      return;
    }
    // Guard against sending empty or $0 quotes. A tradie could otherwise
    // accidentally email a blank quote, which is confusing to the customer
    // and hard to recover from. We require both a positive subtotal and at
    // least one described line item before allowing send.
    if (subtotal <= 0) {
      showToast(
        "Add at least one line item with an amount before sending.",
        "error"
      );
      return;
    }
    if (!lineItems.some((item) => (item.description || "").trim())) {
      showToast(
        "Add a description to at least one line item before sending.",
        "error"
      );
      return;
    }

    const outgoingQuoteNotes = getOutgoingQuoteNotes();

    setSaving(true);

    try {
      // 1. Save quote to Supabase (shows "Quote saved." toast internally).
      // saveQuote returns the freshly-inserted row so we can read the
      // public_token immediately, without waiting for React state to
      // propagate from the parent useLeads hook.
      const savedQuoteRow = await saveQuote(selected.id, {
        subtotal,
        gst,
        total,
        lineItems,
        notes: outgoingQuoteNotes,
        gstRegistered,
        status: "sent",
        sentAt: new Date().toISOString(),
      });

      // Resolve a usable quote number BEFORE generating the PDF so the
      // PDF embeds the same number that the email will reference. Prefer
      // the real sequential number from Supabase; fall back to a
      // deterministic ID derived from the lead UUID.
      const fallbackQuoteNumber = selected?.id
        ? `RQ-${String(selected.id).slice(0, 8).toUpperCase()}`
        : "RQ-DRAFT";
      let quoteNumberForWebhook =
        savedQuoteRow?.quote_number ||
        selected.quote?.quote_number ||
        fallbackQuoteNumber;

      // Resolve the tradie's email from the auth session (the profiles
      // row doesn't store email — auth.users does).
      const {
        data: { session: authSession },
      } = await supabase.auth.getSession();
      const tradieEmail = authSession?.user?.email || "";

      // Resolved business name — fall back to the tradie's full name when
      // the business_name column is empty so emails/PDFs never read
      // "your tradie".
      const tradieBusinessName =
        profile?.business_name || profile?.full_name || "Your tradie";

      // Build the profile object handed to the PDF generator so it has
      // everything the "Prepared by" block needs.
      const pdfProfile = {
        ...(profile || {}),
        email: tradieEmail,
        business_name: tradieBusinessName,
      };

      // Resolve the customer-facing acceptUrl BEFORE PDF generation so the
      // PDF's "Accept Quote Online" button has the correct link baked in.
      // Same publicToken value is reused later for the email payload.
      const publicTokenForPdf =
        savedQuoteRow?.public_token ||
        selected?.quote?.public_token ||
        null;
      const acceptUrlForPdf =
        publicTokenForPdf && getCustomerQuoteLink
          ? getCustomerQuoteLink(publicTokenForPdf)
          : "";

      // 2. Generate the PDF (best-effort — never block the save)
      let pdfBase64 = null;
      try {
        const pdfResult = await generateQuotePDF({
          lead: selected,
          lineItems,
          notes: outgoingQuoteNotes,
          totals: { subtotal, gst, total },
          gstRegistered,
          profile: pdfProfile,
          quoteNumber: quoteNumberForWebhook,
          acceptUrl: acceptUrlForPdf,
        });
        pdfBase64 = pdfResult.base64;
      } catch (pdfErr) {
        // Non-fatal — webhook will still fire without an attachment
      }

      // 3. Email the customer via the send-quote-email edge function.
      // Quote is already saved — email failures are non-fatal and surface
      // as an error toast so the tradie can retry / contact the customer.
      const customerName =
        selected.form?.fullName || selected.customer || "your customer";
      // Try every plausible field path for the customer's email until we
      // find one that's populated.
      const customerEmail =
        selected.form?.email ||
        selected.email ||
        selected.customer_email ||
        selected.captured_info?.email ||
        selected.lead_details?.email ||
        "";

      if (!customerEmail) {
        showToast(
          `Quote saved, but no customer email is on file for ${customerName}.`,
          "error"
        );
      } else {
        try {
          // Resolve a customer-facing URL for the quote so the email can
          // include a "View quote online" CTA. Falls back to the existing
          // selected.quote.public_token when the saveQuote return shape
          // isn't available (older saveQuote callers).
          const publicToken =
            savedQuoteRow?.public_token ||
            selected?.quote?.public_token ||
            null;
          const quoteUrl = publicToken && getCustomerQuoteLink
            ? getCustomerQuoteLink(publicToken)
            : "";

          const emailPayload = {
            customerEmail,
            customerName,
            tradieName: profile?.full_name || "",
            tradieEmail,
            tradieBusinessName,
            tradiePhone: profile?.phone || "",
            tradieLogoUrl: profile?.logo_url || "",
            tradieAbn: profile?.abn || "",
            tradieLicence: profile?.licence_number || "",
            tradieInsuranceProvider:
              profile?.has_insurance ? profile?.insurance_provider || "" : "",
            tradieInsurancePolicy:
              profile?.has_insurance ? profile?.insurance_policy_number || "" : "",
            quoteNumber: quoteNumberForWebhook,
            totalAmount: total,
            quoteNotes: outgoingQuoteNotes,
            quoteUrl,
            // Pass job context so the email can render a trade/project/suburb
            // summary line and a line-item preview above the price.
            jobTrade: selected.trade || selected.form?.trade || "",
            jobProjectType: selected.type || selected.form?.projectType || "",
            jobSuburb: selected.suburb || selected.form?.suburb || "",
            lineItems: Array.isArray(lineItems)
              ? lineItems.map((it) => ({
                  description: it.description || "",
                  amount: Number(it.amount) || 0,
                }))
              : [],
            pdfBase64,
          };

          const session = authSession;

          const functionUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-quote-email`;

          const response = await fetch(functionUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${session?.access_token || ""}`,
              apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
            },
            body: JSON.stringify(emailPayload),
          });

          if (!response.ok) {
            const errText = await response.text().catch(() => "");
            showToast(
              `Quote saved, but email failed: ${response.status} ${errText}`,
              "error"
            );
          } else {
            showToast(
              `Quote saved and emailed to ${customerName}`,
              "success"
            );
          }
        } catch (err) {
          showToast(
            `Quote saved, but email failed: ${err.message || "unknown error"}`,
            "error"
          );
        }
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleDownloadPDF() {
    if (!selected) return;
    setDownloading(true);
    try {
      const {
        data: { session: authSession },
      } = await supabase.auth.getSession();
      const tradieEmail = authSession?.user?.email || "";
      const tradieBusinessName =
        profile?.business_name || profile?.full_name || "Your tradie";

      const fallbackQuoteNumber = selected?.id
        ? `RQ-${String(selected.id).slice(0, 8).toUpperCase()}`
        : "RQ-DRAFT";
      const outgoingQuoteNotes = getOutgoingQuoteNotes();

      const { blob, filename } = await generateQuotePDF({
        lead: selected,
        lineItems,
        notes: outgoingQuoteNotes,
        totals: { subtotal, gst, total },
        gstRegistered,
        profile: {
          ...(profile || {}),
          email: tradieEmail,
          business_name: tradieBusinessName,
        },
        quoteNumber: selected.quote?.quote_number || fallbackQuoteNumber,
      });
      downloadQuotePDF(blob, filename);
      showToast("Quote PDF downloaded.", "success");
    } catch (err) {
      showToast(`PDF generation failed: ${err.message}`, "error");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <Card>
      <div className="quote-edit-zone">
      <h3>
        <Send size={18} /> Quote builder
      </h3>

      <p className="muted">
        {cashPaymentQuote
          ? "Cash payment selected. GST is not applied and no final invoice is required."
          : gstRegistered
            ? "Build an itemised quote below. Amounts are ex-GST and GST is calculated automatically."
            : "Build an itemised quote below. GST is not applied because you are not GST registered."}
      </p>

      {(quoteScope.length > 0 || progressPaymentsRequested || cashPaymentQuote) && (
        <div className="quote-option-strip">
          {quoteScope.length > 0 && <span>For: {quoteScope.join(", ")}</span>}
          {progressPaymentsRequested && <span>Progress payments required</span>}
          {cashPaymentQuote && <span>Cash quote, no GST invoice</span>}
        </div>
      )}

      {/* ── Column headers (subtle, themed via tokens) ─────────────── */}
      <div
        className="line-item-row"
        style={{
          marginBottom: "6px",
          fontSize: "0.72rem",
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color: "var(--text-soft, #6b5448)",
          fontWeight: 600,
        }}
      >
        <span style={{ flex: 1, paddingLeft: "2px" }}>Description</span>
        <span style={{ width: "120px", flexShrink: 0, textAlign: "right" }}>
          {gstRegistered ? "Amount (ex GST)" : "Amount"}
        </span>
        {/* Spacer to balance the Remove button column on each item row */}
        <span style={{ visibility: "hidden", flexShrink: 0 }} aria-hidden>
          Remove
        </span>
      </div>

      {/* ── Line items ──────────────────────────────────────────────── */}
      <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "12px" }}>
        {lineItems.map((item, index) => (
          <div key={item._id} className="line-item-row">
            <input
              placeholder={item.isDiscount ? "Discount" : "e.g. Extra prep"}
              value={item.description}
              onChange={(e) => updateLineItem(index, "description", e.target.value)}
            />
            <input
              placeholder={item.isDiscount ? "-0.00" : "Amount"}
              value={formatMoneyInput(item.amount)}
              onChange={(e) => updateLineItem(index, "amount", e.target.value)}
              style={item.isDiscount ? { color: "#dc2626" } : undefined}
            />
            <Button secondary onClick={() => removeLineItem(index)}>
              Remove
            </Button>
          </div>
        ))}
      </div>

      <div className="button-row">
        <Button secondary onClick={addLineItem}>
          + Add extras
        </Button>
        <Button secondary onClick={openTemplatePicker}>
          <Layers size={14} /> From template
        </Button>
        <Button secondary onClick={addDiscountItem}>
          + Add discount
        </Button>
        {selected?.quote && !quoteIsDraft && (
          <Button secondary onClick={addVariationItem}>
            + Add variation
          </Button>
        )}
      </div>

      {/* ── Template picker (inline, no overlay) ─────────────────────── */}
      {showTemplatePicker && (
        <div
          style={{
            marginTop: "12px",
            border: "1px solid var(--border, rgba(140,100,80,0.18))",
            borderRadius: "12px",
            padding: "14px 16px",
            background: "var(--surface-soft, #faf5f2)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "10px",
              marginBottom: "10px",
            }}
          >
            <div style={{ fontSize: "0.9rem", fontWeight: 600 }}>
              Add from template
            </div>
            <button
              type="button"
              onClick={() => setShowTemplatePicker(false)}
              aria-label="Close template picker"
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                color: "var(--text-soft, #6b5448)",
                padding: "4px",
              }}
            >
              <X size={16} />
            </button>
          </div>
          {pickerLoading ? (
            <p className="muted small" style={{ margin: 0 }}>
              Loading your templates…
            </p>
          ) : pickerTemplates.length === 0 ? (
            <p className="muted small" style={{ margin: 0 }}>
              No templates yet. Create one in Settings → Templates and it'll
              show up here.
            </p>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "6px",
                maxHeight: "260px",
                overflowY: "auto",
              }}
            >
              {pickerTemplates.map((t) => {
                const items = Array.isArray(t.line_items) && t.line_items.length > 0
                  ? t.line_items
                  : [{ description: t.description || t.name, amount: Number(t.amount) || 0 }];
                const sub = items.reduce((s, it) => s + (Number(it.amount) || 0), 0);
                const subtitle = items.length === 1
                  ? items[0].description
                  : `${items.length} line items`;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => addLineItemFromTemplate(t)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "10px",
                      padding: "10px 12px",
                      border: "1px solid var(--border, rgba(140,100,80,0.14))",
                      borderRadius: "10px",
                      background: "var(--surface, #fff)",
                      cursor: "pointer",
                      textAlign: "left",
                      fontFamily: "inherit",
                      fontSize: "0.9rem",
                      color: "var(--text)",
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600 }}>{t.name}</div>
                      {subtitle && (
                        <div
                          className="muted small"
                          style={{
                            marginTop: "2px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {subtitle}
                        </div>
                      )}
                    </div>
                    <div
                      style={{
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {`$${Number(sub).toLocaleString("en-AU", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}`}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Totals table ────────────────────────────────────────────── */}
      <div className="totals-table">
        {gstRegistered ? (
          <>
            <div className="totals-row">
              <span className="totals-label">Subtotal (ex GST)</span>
              <span className="totals-amount">{formatMoneyDisplay(subtotal)}</span>
            </div>
            <div className="totals-row">
              <span className="totals-label">GST (10%)</span>
              <span className="totals-amount">{formatMoneyDisplay(gst)}</span>
            </div>
            <div className="totals-row total">
              <span className="totals-label">Total (inc GST)</span>
              <span className="totals-amount">{formatMoneyDisplay(total)}</span>
            </div>
          </>
        ) : (
          <>
            <div className="totals-row total">
              <span className="totals-label">Total</span>
              <span className="totals-amount">{formatMoneyDisplay(total)}</span>
            </div>
            <p className="muted small" style={{ margin: "10px 0 0" }}>
              {cashPaymentQuote
                ? "Cash payment selected. No GST invoice will be created."
                : "GST not applicable — supplier not GST registered."}
            </p>
          </>
        )}
      </div>

      {/* ── Notes ───────────────────────────────────────────────────── */}
      <div className="full" style={{ marginTop: "18px" }}>
        <label>Quote notes / assumptions</label>
        <textarea
          value={quoteNotes}
          onChange={(e) => setQuoteNotes(e.target.value)}
          placeholder="Includes labour, materials, prep, exclusions, timing, and anything the customer should know."
        />
        {selected?.quote?.notes && selected.quote.notes === quoteNotes && (
          <div className="quote-notes-synced-hint">✓ This matches what you sent to the customer. Edit to prepare a revised quote.</div>
        )}
      </div>

      {/* ── Save & send button ──────────────────────────────────────── */}
      <div className="button-row">
        {canSaveDraft && (
          <Button secondary onClick={handleSaveDraft} disabled={saving}>
            <Save size={15} />
            {saving ? "Saving..." : "Save draft"}
          </Button>
        )}

        <Button onClick={handleSaveAndSend} disabled={saving}>
          <Send size={15} />
          {saving
            ? "Sending…"
            : selected?.quote && !quoteIsDraft
              ? "Save & resend amended quote"
              : "Save & send quote to customer"}
        </Button>

        <Button
          secondary
          onClick={handleDownloadPDF}
          disabled={downloading || saving}
        >
          <Download size={15} />
          {downloading ? "Generating…" : "Download quote PDF"}
        </Button>

        {!quoteIsDraft && selected?.quote?.public_token && (
          <Button
            secondary
            disabled={saving}
            onClick={() => {
              const link = getCustomerQuoteLink(selected.quote.public_token);
              navigator.clipboard.writeText(link);
              showToast("Customer quote link copied.", "success");
            }}
          >
            Copy customer link
          </Button>
        )}
      </div>

      {/* Read-only preview of the customer link so the tradie can verify
          what they're sharing before pasting it into a message. */}
      {!quoteIsDraft && selected?.quote?.public_token && getCustomerQuoteLink && (
        <div className="customer-link-preview">
          <label className="muted small" htmlFor="customer-quote-link-preview">
            Customer link
          </label>
          <input
            id="customer-quote-link-preview"
            type="text"
            readOnly
            value={getCustomerQuoteLink(selected.quote.public_token)}
            onFocus={(e) => e.target.select()}
            aria-label="Customer quote link — read only"
            style={{ width: "100%", marginTop: "6px", fontSize: "0.85rem" }}
          />
        </div>
      )}

      </div>

      {/* ── Saved quote summary ─────────────────────────────────────── */}
      {selected?.quote && (
        <div className="quote-sent-zone">
          <h4 className="quote-sent-heading">
            {quoteIsDraft ? "Draft quote" : "Last sent to customer"}
          </h4>
          <div className="estimate-box" style={{ marginTop: "18px" }}>
            <div className="muted small" style={{ marginBottom: "6px" }}>
              {quoteIsDraft ? "Saved draft" : "Last saved quote"}
            </div>
            <div className="estimate-range">{selected.quote.price || "$0.00"}</div>
            {selected.quote.notes && (
              <p className="muted" style={{ margin: "8px 0 10px", fontSize: "0.85rem" }}>
                {selected.quote.notes}
              </p>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
              <CustomerStatusBadge status={quoteIsDraft ? "draft" : selected.quote.customer_status} />
              {!quoteIsDraft && selected.quote.view_count > 0 && (
                <ReadReceiptBadge
                  viewCount={selected.quote.view_count}
                  lastViewedAt={selected.quote.last_viewed_at || selected.quote.viewed_at}
                />
              )}
              {!quoteIsDraft && selected.quote.view_count === 0 && selected.quote.public_token && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    fontSize: "0.78rem",
                    color: "var(--text-faint, #a8897c)",
                    fontStyle: "italic",
                  }}
                >
                  Not yet opened
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
